import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { SiteContext } from "../site/SiteContext";
import stats from "./content/site-stats.json";
import { DRAFT_SERVICES, SERVICE_ORDER } from "./content/draft-services";

export const DRAFT_BASE = "";
const D = (p) => `${DRAFT_BASE}${p}` || "/";

const NAV = [
  { label: "How we work", to: "/how-we-work" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

function ApplyButton({ className = "", testId }) {
  const { pathname } = useLocation();
  const ref = pathname.replace(DRAFT_BASE, "") || "/";
  return (
    <Link
      to={`${DRAFT_BASE}/apply?ref=${encodeURIComponent(ref)}`}
      data-testid={testId}
      className={`rounded-full bg-pink px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-pink-dark ${className}`}
    >
      Work with us
    </Link>
  );
}

function ServicesMenu() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        data-testid="draft-nav-services"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-sm font-medium text-white/80 transition-colors hover:text-white"
      >
        Services <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid="draft-services-dropdown" className="absolute left-0 top-full pt-3">
          <div className="w-56 rounded-2xl border border-white/15 bg-navy/95 p-2 shadow-2xl backdrop-blur-2xl">
            {SERVICE_ORDER.map((k) => (
              <Link
                key={k}
                to={D(`/${k}`)}
                data-testid={`draft-nav-service-${k}`}
                onClick={() => setOpen(false)}
                className="block rounded-lg px-4 py-2.5 text-sm font-bold text-white/80 hover:bg-white/10 hover:text-white"
              >
                {DRAFT_SERVICES[k].name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DraftHeader() {
  const [mobile, setMobile] = useState(false);
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const onCase = /\/(case-studies(\/[^/]+)?|paid-traffic|ad-creative|retention|aeo)$/.test(pathname);
  useEffect(() => {
    if (!onCase) return;
    const onScroll = () => setScrolled(window.scrollY > 480);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onCase]);
  const pitch = onCase && scrolled;
  return (
    <header data-testid="draft-header" className="sticky top-0 z-40 border-b border-white/10 bg-navy/90 shadow-[0_12px_36px_rgba(7,0,93,0.25)] backdrop-blur-xl">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
        <Link to={D("/")} data-testid="draft-header-logo" aria-label="Smart Marketer Agency home">
          <img src="/assets/logos/logo-white.png" alt="Smart Marketer Agency" className="h-6 w-auto md:h-7" />
        </Link>
        <nav className="hidden items-center gap-8 md:flex">
          {pitch ? (
            <div data-testid="draft-header-pitch" className="ml-10 flex items-center gap-3 whitespace-nowrap text-sm">
              <span className="font-bold text-white">Want results like these in your account?</span>
              <span className="hidden text-white/60 xl:inline">Five-minute fit check, then a call with Pep.</span>
            </div>
          ) : (
            <>
              <ServicesMenu />
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={D(n.to)}
                  data-testid={`draft-nav-${n.to.slice(1)}`}
                  className={({ isActive }) => `text-sm font-medium transition-colors ${isActive ? "text-white" : "text-white/80 hover:text-white"}`}
                >
                  {n.label}
                </NavLink>
              ))}
            </>
          )}
          <ApplyButton testId="draft-header-work-with-us" className="whitespace-nowrap" />
        </nav>
        <div className="flex items-center gap-3 md:hidden">
          <ApplyButton testId="draft-header-work-with-us-mobile" className="px-4 py-2 text-xs" />
          <button data-testid="draft-mobile-menu-toggle" aria-label="Menu" onClick={() => setMobile((m) => !m)} className="text-white">
            {mobile ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      {mobile && (
        <div data-testid="draft-mobile-menu" className="border-t border-white/10 bg-navy px-5 pb-6 md:hidden">
          <div className="pt-4 text-xs font-bold uppercase tracking-widest text-white/50">Services</div>
          {SERVICE_ORDER.map((k) => (
            <Link key={k} to={D(`/${k}`)} onClick={() => setMobile(false)} className="block py-2 text-base text-white">
              {DRAFT_SERVICES[k].name}
            </Link>
          ))}
          <div className="mt-3 border-t border-white/10 pt-3">
            {NAV.map((n) => (
              <Link key={n.to} to={D(n.to)} onClick={() => setMobile(false)} className="block py-2 text-base text-white">
                {n.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

function DraftFooter() {
  const col = "text-sm text-navy/70 hover:text-navy";
  return (
    <footer data-testid="draft-footer" className="border-t border-mist bg-white">
      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 md:grid-cols-4">
        <div>
          <img src="/assets/logos/logo-dark.png" alt="Smart Marketer Agency" className="h-7 w-auto" />
          <a href={`mailto:${stats.email}`} className="mt-4 block text-sm font-medium text-link">{stats.email}</a>
          <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2 text-xs font-bold uppercase tracking-widest text-navy/60">
            <a href={stats.links.facebook} className="hover:text-navy">Facebook</a>
            <a href={stats.links.instagram} className="hover:text-navy">Instagram</a>
            <a href={stats.links.x} className="hover:text-navy">X</a>
            <a href={stats.links.youtube} className="hover:text-navy">YouTube</a>
          </div>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-navy">Company</div>
          <div className="mt-4 flex flex-col gap-2">
            <Link to={D("/how-we-work")} className={col}>How we work</Link>
            <Link to={D("/paid-traffic")} className={col}>Services</Link>
            <Link to={D("/about")} className={col}>About</Link>
            <a href={stats.links.careers} className={col}>Careers</a>
            <Link to={D("/contact")} className={col}>Contact</Link>
          </div>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-navy">Resources</div>
          <div className="mt-4 flex flex-col gap-2">
            <Link to={D("/case-studies")} className={col}>Case Studies</Link>
            <a href={stats.links.blog} className={col}>Smart Marketer blog</a>
            <a href={stats.links.youtube} className={col}>YouTube</a>
          </div>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-navy">Legal</div>
          <div className="mt-4 flex flex-col gap-2">
            <Link to={D("/terms-of-use")} data-testid="footer-terms" className={col}>Terms of Use</Link>
            <Link to={D("/messaging-terms")} data-testid="footer-messaging" className={col}>Messaging Terms</Link>
            <Link to={D("/privacy-policy")} data-testid="footer-privacy" className={col}>Privacy Policy</Link>
          </div>
        </div>
      </div>
      <div className="border-t border-mist">
        <div className="mx-auto max-w-6xl px-5 py-5 text-xs text-navy/50">© 2026 Smart Marketer Agency</div>
      </div>
    </footer>
  );
}

export function DraftLayout() {
  return (
    <SiteContext.Provider value={{ casesBase: D("/case-studies"), homePath: D("/"), applyUrl: D("/apply"), tracked: true }}>
      <div data-testid="draft-site">
        <DraftHeader />
        <Outlet />
        <DraftFooter />
      </div>
    </SiteContext.Provider>
  );
}

export { D as draftPath };
